import { CheckCircle2Icon, TestTube2Icon, TriangleIcon } from "lucide-react";

import { extendResult } from "@blis/shared/models/result";

import { TestSpecLabel } from "@/components/TestSpecLabel";
import { UserTimestamp } from "@/components/UserTimestamp";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SubHeading } from "@/components/ui/heading";
import { Modal } from "@/components/ui/modal";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import type { TTestOrder, TTestOrderResult } from "@/lib/types";
import { formatDecimalDisplay } from "@/lib/utils/number";
import { convertUnitsToSuperscript } from "@/lib/utils/units";
import {
  incompleteReasonDescription,
  incompleteReasonSummary,
} from "../../lib/incomplete-reason";
import { useSetDialogState } from "../detail/state";

export function TestResultDetailsDialog({
  result: resultBase,
  order,
  ...props
}: {
  order: TTestOrder;
  result: TTestOrderResult;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const result = extendResult(resultBase);
  const name =
    result.testResultSpec.name === order.testSpec.name
      ? order.testSpec.name
      : `${order.testSpec.name} - ${result.testResultSpec.name}`;

  const initialResult =
    result.result?.description ||
    result.resultNumeric?.toString() ||
    result.resultHla?.join(", ");

  return (
    <Modal isOpen={props.open} onOpenChange={props.onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Test result details</DialogTitle>
          <SubHeading>
            <TestSpecLabel name={name} code={order.testSpec.code} />
          </SubHeading>
        </DialogHeader>
        <DialogBody>
          <DataList>
            {result.testResultSpec.type === "TEXT" && (
              <DataListItem>
                <DataListLabel>Reference range</DataListLabel>
                <DataListValue>
                  <BaseTestResultBadge
                    result={
                      result.testResultSpec.resultRangeSpecs.find(
                        (rs) => rs.isReference,
                      )?.textResult
                    }
                  />
                </DataListValue>
              </DataListItem>
            )}
            {result.testResultSpec?.type === "NUMERIC" && (
              <>
                <DataListItem>
                  <DataListLabel>Reportable range</DataListLabel>
                  <DataListValue>
                    <div className="flex gap-4">
                      <Text size="2">
                        Min: {result.testResultSpec.rangeMin?.toString() ?? "-"}
                      </Text>
                      <Text size="2">
                        Max: {result.testResultSpec.rangeMax?.toString() ?? "-"}
                      </Text>
                    </div>
                  </DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Reference range</DataListLabel>
                  <DataListValue>
                    {result.testResultSpec.resultRangeSpecs &&
                      result.testResultSpec.resultRangeSpecs.length > 0 && (
                        <ul>
                          {result.testResultSpec.resultRangeSpecs.map((rr) => (
                            <li key={rr.id}>
                              ({rr.rangeLow?.toString()} -{" "}
                              {rr.rangeHigh?.toString()})
                              {rr.sex && ` (${rr.sex})`}
                              {(rr.ageLow || rr.ageHigh) &&
                                ` (${rr.ageLow || ""}-${rr.ageHigh || ""})`}
                            </li>
                          ))}
                        </ul>
                      )}
                  </DataListValue>
                </DataListItem>
              </>
            )}
          </DataList>

          <Separator />
          <DataList>
            <DataListItem>
              <DataListLabel>Status</DataListLabel>
              <DataListValue>
                <Text size="2" className="first-letter:capitalize">
                  {resultBase.status}
                </Text>
              </DataListValue>
            </DataListItem>
            {resultBase.status === "reflex pending" &&
              resultBase.incompleteReason && (
                <DataListItem>
                  <DataListLabel>Waiting on</DataListLabel>
                  <DataListValue>
                    <div className="flex flex-col gap-1">
                      <Text size="2">
                        {incompleteReasonSummary(resultBase.incompleteReason)}
                      </Text>
                      <Text size="1" muted>
                        {incompleteReasonDescription(
                          resultBase.incompleteReason,
                        )}
                      </Text>
                    </div>
                  </DataListValue>
                </DataListItem>
              )}
            <DataListItem>
              <DataListLabel>Initial result</DataListLabel>
              <DataListValue>
                {result.$initial &&
                  (result.resultFreeText?.trim() ? (
                    <Text elementType="pre" size="1">
                      {result.resultFreeText}
                    </Text>
                  ) : (
                    <BaseTestResultBadge
                      result={result.result || initialResult}
                      units={result.testResultSpec.units}
                      noColor={result.testResultSpec.type === "HLA"}
                    />
                  ))}
              </DataListValue>
            </DataListItem>
            <DataListItem>
              <DataListLabel>Entered by</DataListLabel>
              <DataListValue>
                {result.$initial && (
                  <UserTimestamp
                    user={result.enteredBy || result.enteredByInstrument}
                    timestamp={result.enteredAt}
                  />
                )}
              </DataListValue>
            </DataListItem>
            <DataListItem className="items-center">
              <DataListLabel>Validated</DataListLabel>
              <DataListValue>
                {result.validated && (
                  <CheckCircle2Icon className="text-indigo-600 size-4" />
                )}
              </DataListValue>
            </DataListItem>
            <DataListItem>
              <DataListLabel>Validated by</DataListLabel>
              <DataListValue>
                {result.validated && (
                  <UserTimestamp
                    user={result.validatedBy}
                    timestamp={result.validatedAt}
                  />
                )}
              </DataListValue>
            </DataListItem>
            <DataListItem>
              <DataListLabel>Final result</DataListLabel>
              <DataListValue>
                {result.$final &&
                  (result.finalResultFreeText?.trim() ? (
                    <Text elementType="pre" size="1">
                      {result.finalResultFreeText}
                    </Text>
                  ) : (
                    <BaseTestResultBadge
                      result={result.finalResult || result.$final?.toString()}
                    />
                  ))}
                {(result.cancelled || order.cancelled) && (
                  <Badge className="ml-2">Cancelled</Badge>
                )}
              </DataListValue>
            </DataListItem>
          </DataList>
          <DeltaCheckSection
            conflicts={resultBase.conflicts}
            units={result.testResultSpec.units}
          />
          <Separator />
          <DataList>
            <DataListItem>
              <DataListLabel>
                Sample {result.$initial ? "used" : "assigned"}
              </DataListLabel>
              <DataListValue>
                <SampleDisplay
                  result={result}
                  resultBase={resultBase}
                  order={order}
                />
              </DataListValue>
            </DataListItem>

            <DataListItem>
              <DataListLabel>Comment</DataListLabel>
              <DataListValue>
                {result.comment && (
                  <Text size="1" elementType="pre">
                    {result.comment}
                  </Text>
                )}
              </DataListValue>
            </DataListItem>
            <DataListItem>
              <DataListLabel>Comment by</DataListLabel>
              <DataListValue>
                {result.commentBy && (
                  <UserTimestamp
                    user={result.commentBy}
                    timestamp={result.commentCreatedAt}
                  />
                )}
              </DataListValue>
            </DataListItem>
          </DataList>

          {result.cancelled && result.cancelledReason ? (
            <>
              <Separator />
              <DataList>
                <DataListItem>
                  <DataListLabel>Cancelled</DataListLabel>
                  <DataListValue>
                    <Text size="1" elementType="pre">
                      {result.cancelledReason}
                    </Text>
                  </DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Cancelled by</DataListLabel>
                  <DataListValue>
                    <UserTimestamp
                      user={result.cancelledBy}
                      timestamp={result.cancelledAt}
                    />
                  </DataListValue>
                </DataListItem>
              </DataList>
            </>
          ) : order.cancelled ? (
            <>
              <Separator />
              <DataList>
                <DataListItem>
                  <DataListLabel>Cancelled reason</DataListLabel>
                  <DataListValue>
                    <Text size="1" elementType="pre">
                      {order.cancelledReason}
                    </Text>
                  </DataListValue>
                </DataListItem>
                <DataListItem>
                  <DataListLabel>Cancelled by</DataListLabel>
                  <DataListValue>
                    <UserTimestamp
                      user={order.cancelledBy}
                      timestamp={order.cancelledAt}
                    />
                  </DataListValue>
                </DataListItem>
              </DataList>
            </>
          ) : null}
        </DialogBody>
        <DialogFooter />
      </Dialog>
    </Modal>
  );
}

function DeltaCheckSection({
  conflicts,
  units,
}: {
  conflicts: TTestOrderResult["conflicts"];
  units: string | null;
}) {
  const deltaConflicts = conflicts?.filter((conflict) => conflict.delta) ?? [];
  if (deltaConflicts.length === 0) {
    return null;
  }

  return (
    <>
      <Separator />
      <DataList>
        {deltaConflicts.map((conflict) => (
          <DeltaCheckDetails
            key={conflict.id}
            conflict={conflict}
            units={units}
          />
        ))}
      </DataList>
    </>
  );
}

function DeltaCheckDetails({
  conflict,
  units,
}: {
  conflict: NonNullable<TTestOrderResult["conflicts"]>[number];
  units: string | null;
}) {
  const delta = conflict.delta;
  if (!delta) {
    return null;
  }

  const compared = delta.comparedToResult;

  if (delta.type === "QUALITATIVE") {
    const previousDescription =
      compared?.finalResult?.description ?? compared?.result?.description;

    return (
      <>
        <DataListItem className="items-center">
          <DataListLabel>Delta check</DataListLabel>
          <DataListValue>
            <Badge color="red" className="gap-1">
              <TriangleIcon className="size-3" />
              Discrepant
            </Badge>
          </DataListValue>
        </DataListItem>
        <DataListItem>
          <DataListLabel>Previous result</DataListLabel>
          <DataListValue>
            <BaseTestResultBadge result={previousDescription} />
          </DataListValue>
        </DataListItem>
        <DeltaCheckComparedToItem accessionId={compared?.accessionId} />
      </>
    );
  }

  if (delta.delta === null) {
    return null;
  }

  const numericDelta = delta.delta;
  // Show the value the delta was actually computed against — the prior
  // accession's finalized value (falling back to the entered value).
  const previousNumeric =
    compared?.finalResultNumeric ?? compared?.resultNumeric;

  return (
    <>
      <DataListItem className="items-center">
        <DataListLabel>Delta check</DataListLabel>
        <DataListValue>
          <Badge color="red" className="gap-1">
            <TriangleIcon className="size-3" />
            {numericDelta.gt(0) && "+"}
            {numericDelta.toFixed(1)}
            {delta.type === "PERCENT" ? "%" : units ? ` ${units}` : ""}
          </Badge>
        </DataListValue>
      </DataListItem>
      {previousNumeric != null && (
        <DataListItem>
          <DataListLabel>Previous result</DataListLabel>
          <DataListValue>
            <BaseTestResultBadge
              result={formatDecimalDisplay(previousNumeric)}
              units={units}
            />
          </DataListValue>
        </DataListItem>
      )}
      <DeltaCheckComparedToItem accessionId={compared?.accessionId} />
    </>
  );
}

function DeltaCheckComparedToItem({
  accessionId,
}: {
  accessionId: number | null | undefined;
}) {
  if (accessionId == null) {
    return null;
  }

  return (
    <DataListItem>
      <DataListLabel>Compared to</DataListLabel>
      <DataListValue>
        <a
          href={`/accessions/${accessionId}`}
          target="_blank"
          rel="noreferrer"
          className="font-medium underline"
        >
          {accessionId}
        </a>
      </DataListValue>
    </DataListItem>
  );
}

function SampleDisplay({
  result,
  resultBase,
  order,
}: {
  result: ReturnType<typeof extendResult>;
  resultBase: TTestOrderResult;
  order: TTestOrder;
}) {
  const setAssignSample = useSetDialogState("assignSample");

  // If result is present and has initial result, display "sample used"
  if (result.$initial && resultBase.sample) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" size="sm" className="shrink-0">
          {resultBase.sample.displayId || resultBase.sample.id}
        </Badge>
        <Text size="2" className="truncate" elementType="p">
          {resultBase.sample.tube.name}
        </Text>
      </div>
    );
  }

  // If result is present but no sample used, display message
  if (result.$initial && !resultBase.sample) {
    return (
      <Text size="2" className="text-muted-foreground">
        No sample recorded
      </Text>
    );
  }

  // If no result present, display sample assignments from TestOrder.samples
  if (!result.$initial && order.samples.length > 0) {
    return (
      <div className="flex items-start gap-2 w-full flex-wrap sm:flex-nowrap">
        <div className="flex flex-col gap-1 flex-1 min-w-0 basis-full sm:basis-auto">
          {order.samples.map((sample) => (
            <div key={sample.sampleId} className="flex items-center gap-2">
              <Badge variant="outline" size="sm" className="shrink-0">
                {sample.sample.displayId || sample.sample.id}
              </Badge>
              <Text size="2" className="truncate" elementType="p">
                {sample.sample.tube.name}
              </Text>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 basis-auto"
          onPress={() => {
            setAssignSample({
              orderId: order.id,
              currentSampleIds: order.samples.map((s) => s.sampleId),
            });
          }}
        >
          <TestTube2Icon />
          Assign
        </Button>
      </div>
    );
  }

  // If no result present and no sample assignment, display Assign button
  if (!result.$initial && order.samples.length === 0) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onPress={() => {
          setAssignSample({
            orderId: order.id,
            currentSampleIds: order.samples.map((s) => s.sampleId),
          });
        }}
      >
        <TestTube2Icon />
        Assign
      </Button>
    );
  }

  return null;
}

type TextResult = {
  value: string;
  description: string;
  color: string | null;
};

export function BaseTestResultBadge({
  result,
  units,
  noColor,
}: {
  result?: string | (string | null)[] | TextResult | null;
  units?: string | null;
  noColor?: boolean;
}) {
  if (!result) {
    return <span>-</span>;
  }
  let resultValue: string | null = null;
  let resultColor: "red" | "gray" = "gray";
  if (typeof result === "string") {
    resultValue = result;
  }
  if (Array.isArray(result)) {
    resultValue = result.filter(Boolean).join(", ");
  } else if (typeof result === "object") {
    resultValue = result.description;
    resultColor = (result.color as typeof resultColor) ?? "gray";
  }
  return (
    <Badge
      color={resultColor && !noColor ? resultColor : "gray"}
      className="gap-1"
    >
      {resultValue}
      {units && <span>{convertUnitsToSuperscript(units)}</span>}
    </Badge>
  );
}
